/**
 * Прайс-offer для калькулятора: компактный срез активной версии прайс-листа
 * (только расчётные таблицы и ценовые колонки) + курс USD на сегодня.
 *
 * Источник правды цены — ИМЕННО прайс (матрица «ткань × карниз/опция × размер»).
 * Калькулятор выбирает ряд (ткань) + опцию (карниз/вариант) + размер, а цену
 * считает бэкенд: GET /v1/orders/:id/calc-preview или сохранение позиции с
 * полем priceBook:{tableId,rowId,optionKey,withoutCornice}.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CorniceMechanismOption, PriceBookAddAnotherConfig } from '@jaluzi/types';
import {
  chainLengthCmFromState,
  chainLengthMetersValue,
  formatChainLengthCm,
  mechanismCorniceKeyOf,
  mechanismFieldEnabledOptions,
  mechanismFieldVisibleForCornice,
  mechanismFieldSliderOptions,
  mechanismTypeAllowedForBranch,
  motorMatchesSection,
  motorMatchesCornice,
  optionUsesMechanismFieldSlider,
  parseChainLengthCm,
  usesMechanismFieldSlider,
  type MechanismCorniceBinding,
} from '@jaluzi/calc';
import type { PbPriceRule, PbSizeLimits } from '@jaluzi/calc/pricing';
import { api } from '../../lib/api.js';
export {
  pbPriceRowsForSelection,
  pbOptionPricedOnColors,
  pbPriceOptionAvailability,
  pbTableForPriceRow,
  pbTablesForPriceRow,
} from './availability.js';

export type PbOptionAuto = 'AREA' | 'WIDTH' | 'RANGE' | null;

/** Правило ступени — та же форма, что у сервера (общая математика в @jaluzi/calc/pricing). */
export type PbColumnRule = PbPriceRule;

/** Опция выбора (карниз/вариант) — то, что выбирает замерщик. */
export interface PbOption extends CorniceMechanismOption {
  key: string;
  bindingKey: string;
  label: string;
  auto: PbOptionAuto;
  columns: PbColumnRule[];
  /** Тип карниза (Гэп-2) — для группировки шага «карниз → цвет». */
  groupLabel?: string | null;
  /** Цвет карниза (Гэп-2) — если у типа несколько цветов. */
  colorLabel?: string | null;
  /** Разрешённые цвета именно этой опции; [] = цвет не нужен. */
  corniceColors?: string[];
  /** Размер планки обязателен; у Плиссе поле может быть видимым, но необязательным. */
  requiresSlatSize?: boolean;
  /** Выбор «С планкой / Без планки». */
  requiresSlatChoice?: boolean;
  slatChoiceAsCorniceType?: boolean | null;
  /** NONE = эта ветка не должна показывать шаг механизма. */
  mechanismMode?: 'INHERIT' | 'NONE' | 'FIELDS_ONLY' | 'MOTORS_ONLY';
  /** Точный allow-list типов механизма для этой ветки. */
  mechanismTypeIds?: string[];
  /** Подписи полей и вариантов этой ветки. Вариант: «поле::канонический вариант». */
  mechanismFieldLabels?: Record<string, string>;
  /** «Есть размер борта» — калькулятор спросит доп-поле «Размер борта, мм». */
  requiresBortSize?: boolean;
  /** «Есть бахрома» — калькулятор спросит «С бахромой / Без бахромы». Размера НЕТ. */
  requiresBahroma?: boolean;
  /** Скрыт из калькулятора (кнопка «Скрыть» в «Расчёте») — в мастере не показываем.
   *  Резолвер цены на бэке его всё равно знает, поэтому старые заказы считаются. */
  hidden?: boolean;
  /** Фиксированная опция без диапазона. В MOUNT_BASE цвет базового карниза не нужен. */
  fixed?: boolean;
  /** Самостоятельный итоговый тариф «Без карниза». */
  withoutCornice?: boolean;
  /** Ограничения размера этого варианта (Межстекольные) — предупреждение, на цену не влияют. */
  sizeLimits?: PbSizeLimits;
}

export interface PbOfferCell {
  /** priceCents (USD-центы). */
  p: number | null;
}

export interface PbOfferRow {
  id: string;
  groupLabel: string | null;
  label: string;
  label2: string | null;
  /** Цвет → id СВОЕЙ ленты. Точная связь: имя ленты неуникально («Деревянные жалюзи
   *  PVS» — три строки с ценами 170/200/140). null у самих лент и у старых строк. */
  parentRowId?: string | null;
  /** Стабильный id строки между версиями (нет у старого кеша — тогда берём id). */
  logicalId?: string;
  /** Типы механизма этой позиции; пусто = родитель/ветка карниза. */
  mechanismTypeIds?: string[];
  isNew: boolean;
  /** Скрыта в калькуляторе (галочка в «Связках») — в мастере не показываем. */
  calcHidden?: boolean;
  /**
   * Карниз, заданный атрибутом строки прайса, а не ценовой колонкой.
   * Опционально для совместимости с уже закэшированным старым offer.
   */
  optionLabel?: string | null;
  /** columnId → ячейка. */
  cells: Record<string, PbOfferCell>;
}

export interface PbOfferTable {
  id: string;
  title: string | null;
  subtitle: string | null;
  rowHeader1: string;
  rowHeader2: string | null;
  /** Где лежит ИМЯ позиции: 1 = rowHeader1 («Ленты»), 2 = rowHeader2 (Текстильные —
   *  «Наименование», Горизонтальные — «Название ленты»: первая колонка там = группа). */
  nameCol?: 1 | 2;
  options: PbOption[];
  columns: Array<{ id: string; index: number; label: string }>;
  rows: PbOfferRow[];
}

export interface PbOfferSection {
  code: string;
  title: string;
  shortTitle: string | null;
  /** CORNICE — обычный выбор карниза; MOUNT_BASE — монтаж + базовый карниз. */
  calcOptionRole?: 'CORNICE' | 'MOUNT_BASE';
  /** MOUNT_COLOR_RANGES — монтаж → цвет карниза → размерная ступень. */
  calcFlow?: 'STANDARD' | 'MOUNT_COLOR_RANGES' | 'BRANCHED_CORNICE'
    | 'BRANCHED_CORNICE_DIRECT' | 'ROW_ONLY' | 'TEXTILE';
  baseCorniceName?: string | null;
  baseCorniceColors?: string[];
  tables: PbOfferTable[];
}

/** Тип механизма (конфиг priceBook.mechanismConfig — данные, не код). */
export interface PbMechanismType {
  id: string;
  name: string;
  /** Скрытый тип хранится для старых изделий, но не предлагается в новом расчёте. */
  hidden?: boolean;
  /** Показывается только в ветках, где его id выбран явно. */
  scoped?: boolean;
  /** NONE = без доп. выбора; MOTORS = мотор из «Дополнительно» с необязательными
   *  списками после него; FIELDS = списки-характеристики без платной позиции. */
  kind: 'NONE' | 'MOTORS' | 'FIELDS';
  /** Списки доступны у FIELDS и MOTORS. У MOTORS показываются после выбора мотора.
   *  cornices — область видимости поля по карнизам-цветам (настраивается в «Связках»):
   *  undefined = для ВСЕХ карнизов; [] = ни для кого; [ключи] = только для этих.
   *  numeric — подмножество options, у которых при выборе замерщик вводит число
   *  (напр. «Кастомный» → длина); unit — подпись единицы (напр. «м»). */
  fields?: Array<{ name: string; options: string[]; enabledOptions?: string[]; corniceDefaultEnabled?: boolean; corniceBindings?: MechanismCorniceBinding[]; numeric?: string[]; unit?: string; slider?: boolean; sliderOptions?: string[] }>;
  /** Для kind MOTORS: доступность моторов по карнизам («Связки» → «Электронный по
   *  карнизам»). Ключ = ИМЯ мотора, значение = ключи карнизов-цветов. Нет ключа =
   *  мотор для ВСЕХ карнизов; [] = ни для кого; [ключи] = только для этих. */
  motorCornices?: Record<string, string[]>;
}

export interface PbOffer {
  versionId: string;
  effectiveFrom: string;
  sections: PbOfferSection[];
  /** Курс USD на сегодня (или null — тогда цена только в долларах). */
  rate: { value: number; source: string; date: string } | null;
  /** Типы механизмов для шага «Механизм» (у старого кэша может отсутствовать). */
  mechanismTypes?: PbMechanismType[];
  /** Поведение «+ Добавить размер»; отсутствие = безопасный старый дефолт. */
  addAnotherConfig?: PriceBookAddAnotherConfig;
}

/** Ключ+загрузчик прайса — общие для хука и предзагрузки в Shell (офлайн-кеш замерщика). */
export const priceBookOfferQuery = {
  queryKey: ['price-book', 'offer'] as const,
  queryFn: () => api<PbOffer>('/v1/price-book/offer'),
};

export function usePriceBookOffer(): UseQueryResult<PbOffer> {
  return useQuery({
    ...priceBookOfferQuery,
    // Держим кэш минуту (чтобы не дёргать на каждом шаге визарда), но при КАЖДОМ
    // открытии калькулятора подтягиваем свежее — иначе правки в «Связках»/прайсе
    // из админки видны только через 5 мин. Кэш показывается сразу, обновление в фоне.
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

/** Убираем из таблицы то, что замерщик не должен видеть:
 *  • строки (ленты), скрытые галочкой «Связок» (calcHidden);
 *  • карнизы (опции), скрытые кнопкой «Скрыть» в «Расчёте» (hidden) — резолвер
 *    цены на бэке их всё равно знает, поэтому старые заказы по ним считаются. */
const visibleRows = (table: PbOfferTable): PbOfferTable =>
  ({
    ...table,
    rows: table.rows.filter((r) => !r.calcHidden),
    options: table.options.filter((o) => !o.hidden),
  });

/**
 * ВСЕ расчётные таблицы раздела. У части разделов их несколько — это варианты
 * монтажа/карниза («Со сверлением», «Без сверления Faltex», …), у каждого свои
 * карнизы и своя матрица цен. Раньше калькулятор брал только первую и молча терял
 * остальные.
 */
export function offerTablesForSection(offer: PbOffer | undefined, sectionCode: string | null | undefined): PbOfferTable[] {
  if (!offer || !sectionCode) return [];
  const section = offer.sections.find((s) => s.code === sectionCode);
  return (section?.tables ?? []).map(visibleRows);
}

export function offerSection(
  offer: PbOffer | undefined,
  sectionCode: string | null | undefined,
): PbOfferSection | null {
  if (!offer || !sectionCode) return null;
  return offer.sections.find((section) => section.code === sectionCode) ?? null;
}

/** Подпись варианта монтажа в списке выбора («Со сверлением»). */
export function pbTableLabel(table: PbOfferTable): string {
  return table.subtitle?.trim() || table.title?.trim() || '—';
}

/**
 * Выбранная таблица раздела. Если `tableId` не задан (новое изделие) или не найден
 * (таблицу удалили из прайса) — первая: у разделов с одной таблицей поведение прежнее.
 */
export function pbTableForSection(
  offer: PbOffer | undefined,
  sectionCode: string | null | undefined,
  tableId: string | null | undefined,
): PbOfferTable | null {
  const tables = offerTablesForSection(offer, sectionCode);
  if (!tables.length) return null;
  return (tableId ? tables.find((t) => t.id === tableId) : null) ?? tables[0]!;
}

/** @deprecated Берёт первую таблицу — используйте pbTableForSection с pbTableId из state. */
export function offerTableForSection(offer: PbOffer | undefined, sectionCode: string | null | undefined): PbOfferTable | null {
  return pbTableForSection(offer, sectionCode, null);
}

/** Механизм по изделию (Гэп-1): моторчики/муфты раздела (ACCESSORY-строки прайса). */
export interface PbAccessory {
  rowId: string;
  name: string;
  sectionCode: string;
  section: string;
  /** Human-readable table title; never used as a relationship key. */
  table?: string | null;
  /** Stable link to the mechanism type; table is display-only. */
  mechanismTypeId?: string | null;
  /** Stable product-section links (for example, `ROLLO`). */
  mechanismSectionCodes?: string[];
  /** AUTO-строки считаются сервером и не являются выбираемыми моторчиками. */
  mechanismPriceMode?: 'AUTO' | null;
  mechanismPriceUnit?: 'PER_SQM' | 'PER_ITEM' | 'PER_RUN_M' | null;
  mechanismTargetRowLogicalIds?: string[];
  priceUsdCents: number;
  /** Как priceUsdCents, но null = цена не заполнена (нет у старого кеша). */
  priceCents?: number | null;
}

export const priceBookAccessoriesQuery = {
  queryKey: ['price-book', 'accessories'] as const,
  queryFn: () => api<PbAccessory[]>('/v1/price-book/accessories'),
};

export function usePriceBookAccessories(): UseQueryResult<PbAccessory[]> {
  return useQuery({
    ...priceBookAccessoriesQuery,
    // Моторы/связки правят в админке — при открытии калькулятора всегда берём свежее
    // (иначе выбор в «Связках» виден только через 5 мин). Кэш минуту + refetch на mount.
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

/** Моторы раздела вида (по pbSectionCode). Пустой массив — механизм не предлагается. */
export function motorsForSection(accs: PbAccessory[] | undefined, sectionCode: string | null | undefined): PbAccessory[] {
  if (!accs || !sectionCode) return [];
  return accs.filter((a) => a.sectionCode === sectionCode);
}

/** Моторы для шага «Механизм»: единый раздел прайса «Механизмы» (MECHANISMS) —
 *  клиент ведёт виды и цены там. Фолбэк — легаси-строки «Дополнительно» самого
 *  раздела вида (пока прод/оффлайн-кэш не мигрированы). */
export function motorsForCalc(accs: PbAccessory[] | undefined, sectionCode: string | null | undefined): PbAccessory[] {
  const central = (accs ?? []).filter((a) => a.sectionCode === 'MECHANISMS' && a.mechanismPriceMode !== 'AUTO');
  if (central.length) return central;
  return motorsForSection(accs, sectionCode);
}

/** Моторы ВЫБРАННОГО типа: в разделе MECHANISMS таблица = тип механизма, фильтруем
 *  по её заголовку (Электронный ↔ таблица «Электронный»). Нет совпадений (легаси-
 *  строки/старый кэш без table, либо клиент переименовал таблицу) — весь список. */
export function motorsForType(motors: PbAccessory[], typeId: string | null | undefined): PbAccessory[] {
  if (!typeId) return motors;
  return motors.filter((motor) => motor.mechanismTypeId === typeId);
}

/** Фильтр моторов по ВИДУ жалюзи — ЖЁСТКИЙ (решение заказчика): показываем ТОЛЬКО
 *  моторы, ЯВНО привязанные к виду в «Связках» (mechanismSectionCodes содержит код вида).
 *  Ни авто-подбора по названию, ни «универсальных» — «нет такого понятия как
 *  универсал»: если мотор не отмечен у вида, значит он к нему не относится.
 *  Пусто → у вида нет моторов → шаг «Механизм» = «Без механизма». */
export function motorsForVid(
  motors: PbAccessory[],
  _categoryName: string | null | undefined,
  categorySectionCode?: string | null,
): PbAccessory[] {
  return motors.filter(m => motorMatchesSection(m, categorySectionCode));
}

/** ВТОРОЙ фильтр моторов — по КАРНИЗУ («Связки» → «Моторы по карнизам»).
 *  Область хранится в конфиге типа механизма: types[].motorCornices[rowId мотора].
 *  Нет ключа = мотор для ВСЕХ карнизов; [] = ни для кого; [ключи] = только для этих.
 *  Карниз ещё не выбран (corniceKey пуст) — не режем (список появится после выбора).
 *  Применяется ПОСЛЕ motorsForVid: мотор виден, если подходит и виду, и карнизу. */
export function motorsForCornice(
  motors: PbAccessory[],
  types: PbMechanismType[] | undefined,
  corniceKey: string | null,
): PbAccessory[] {
  if (!types?.length) return motors;
  return motors.filter((m) => {
    const tp = types.find((x) => x.kind === 'MOTORS' && x.id === m.mechanismTypeId);
    return !!tp && motorMatchesCornice(m, tp, corniceKey);
  });
}

// ─── Типы механизма (конфиг priceBook.mechanismConfig) ─────────────────────
// Общая логика мастера (CalcWizard) и плоских форм (CalcFlat): список типов,
// активный тип, подпись-снапшот и валидность выбора.

/** Типы из offer; фолбэк «ручной+моторы» для старого оффлайн-кэша. Базовые правила:
 *  • NONE («Без механизма») и FIELDS («Механический» — цепочка со своими полями,
 *    моторы НЕ нужны) — не зависят от наличия моторов;
 *  • MOTORS («Электронный») — только если под него в «Связках» привязаны моторы
 *    этого типа (motor.mechanismTypeId = type.id).
 *  После выбора карниза вызывающий код отдельно убирает FIELDS-тип, если для него
 *  сняты все галочки в «Механизм по карнизам». */
export function mechanismTypesFrom(
  offer: PbOffer | undefined,
  motors: PbAccessory[],
  fallback: { manual: string; electric: string },
  retained?: { id?: string | null; label?: string | null },
  mechanismTypeIds?: string[],
): PbMechanismType[] {
  const raw: PbMechanismType[] = offer?.mechanismTypes?.length
    ? offer.mechanismTypes
    : [
        { id: 'manual', name: fallback.manual, kind: 'NONE' },
        { id: 'electric', name: fallback.electric, kind: 'MOTORS' },
      ];
  const ids = mechanismTypeIds ?? [];
  return raw
    .filter((x) => {
      const retainedForEdit = x.id === retained?.id
        || (!!retained?.label && retained.label.startsWith(x.name));
      if (!mechanismTypeAllowedForBranch(x, ids) && !retainedForEdit) return false;
      if (x.hidden && !retainedForEdit) return false;
      if (x.kind === 'NONE' || x.kind === 'FIELDS') return true;
      return motors.some((motor) => motor.mechanismTypeId === x.id);
    });
}

/** Активный тип: явный выбор → восстановление из данных изделия (мотор или
 *  подпись mechanismName, начинающаяся с имени типа) → первый видимый и
 *  доступный тип в глобальном порядке Конструктора. Последний вариант является
 *  дефолтом нового расчёта: его моторы или поля раскрываются сразу. */
export function activeMechanismType(
  types: PbMechanismType[],
  s: { pbMechTypeId: string | null; pbMechRowId: string | null; pbMechLabel: string | null },
): PbMechanismType | null {
  const legacyWoodManual = s.pbMechTypeId === 'wood-thread'
    || s.pbMechTypeId === 'wood-chain'
    || /^(Ниточный|Цепочный)(?:\s|\()/u.test(s.pbMechLabel ?? '');
  return types.find((x) => x.id === s.pbMechTypeId)
    ?? (legacyWoodManual ? types.find((x) => x.id === 'mechanical') : undefined)
    ?? (s.pbMechRowId ? types.find((x) => x.kind === 'MOTORS') : undefined)
    ?? (s.pbMechLabel ? types.find((x) => s.pbMechLabel!.startsWith(x.name)) : undefined)
    ?? types[0]
    ?? null;
}

/** Стабильный ключ строки «Связок»: обычный цветовой карниз использует
 *  «Тип · Цвет», а MOUNT_BASE-ветка — свой label для всех внутренних цветов. */
export function corniceKeyOf(opt: PbOption | null | undefined): string | null {
  return mechanismCorniceKeyOf(opt);
}

/** Видимые поля механизма для выбранной строки связки. Одинаково работает для
 *  самостоятельного FIELDS-типа и для списков после позиции MOTORS-типа.
 *  Точная пара sectionCode + stable corniceKey; для новых веток действует
 *  явный corniceDefaultEnabled. */
export function visibleMechFields(
  tp: PbMechanismType | null,
  corniceKey: string | null,
  sectionCode?: string | null,
): NonNullable<PbMechanismType['fields']> {
  const fs = tp?.fields ?? [];
  return fs.filter((field) => mechanismFieldVisibleForCornice(field, corniceKey, sectionCode));
}

export function mechanismFieldDisplayLabel(
  fieldName: string,
  labels: Record<string, string> = {},
): string {
  return labels[fieldName]?.trim() || fieldName;
}

export function mechanismOptionDisplayLabel(
  fieldName: string,
  option: string,
  labels: Record<string, string> = {},
): string {
  return labels[`${fieldName}::${option}`]?.trim() || option;
}

/** Прежние названия остаются только в готовых подписях уже созданных изделий.
 *  Конфиг хранит одно универсальное поле, а при восстановлении принимаем все
 *  исторические варианты его названия. */
function legacyMechanismFieldNames(fieldName: string): string[] {
  if (fieldName === 'Сторона управления') {
    return ['Сторона расположения', 'Сторона расположения цепочки'];
  }
  if (fieldName === 'Длина управления') {
    return ['Длина цепочки', 'Длина нити'];
  }
  return [];
}

function mechanismOptionMatches(
  fieldName: string,
  option: string,
  raw: string,
  labels: Record<string, string>,
): boolean {
  if (option === raw || mechanismOptionDisplayLabel(fieldName, option, labels) === raw) {
    return true;
  }
  if (option === 'Кастомная' && raw === 'Кастомный') return true;
  if (option === 'Кронштейн длинный 15 см' && raw === 'Кронштейн длинный 15см') return true;
  return false;
}

/**
 * Старые изделия хранят только готовую подпись mechanismName. Восстанавливаем
 * из неё структурные поля перед редактированием, чтобы открытие новой шторки не
 * заменяло сохранённые значения дефолтами.
 */
export function mechanismFieldStateFromLabel(
  tp: PbMechanismType | null,
  label: string | null | undefined,
  corniceKey: string | null = null,
  fieldLabels: Record<string, string> = {},
  sectionCode?: string | null,
): { fields: Record<string, string>; values: Record<string, string> } {
  const fields: Record<string, string> = {};
  const values: Record<string, string> = {};
  if (!tp || tp.kind === 'NONE' || !label?.endsWith(')')) {
    return { fields, values };
  }

  // FIELDS хранится как «Механический (…)», MOTORS — как «Мотор 220V (…)».
  // Имя мотора может само содержать скобки, поэтому берём последнюю группу.
  const detailsStart = label.lastIndexOf(' (');
  if (detailsStart < 0) return { fields, values };
  const body = label.slice(detailsStart + 2, -1);
  const parts = body.split(';').map((part) => part.trim());
  const visibleFields = visibleMechFields(tp, corniceKey, sectionCode);
  const visibleFieldNames = new Set(visibleFields.map((field) => field.name));
  for (const field of visibleFields) {
    const enabledOptions = mechanismFieldEnabledOptions(field);
    const prefixes = [...new Set([
      mechanismFieldDisplayLabel(field.name, fieldLabels),
      field.name,
      // Если историческое имя теперь заведено собственной карточкой списка,
      // универсальное поле не должно перехватывать её сохранённое значение.
      ...legacyMechanismFieldNames(field.name)
        .filter((legacyName) => !visibleFieldNames.has(legacyName)),
    ])].map((fieldName) => `${fieldName}:`);
    const part = parts.find((candidate) =>
      prefixes.some((prefix) => candidate.startsWith(prefix)));
    if (!part) continue;
    const prefix = prefixes.find((candidate) => part.startsWith(candidate))!;
    const raw = part.slice(prefix.length).trim();

    const exactOption = enabledOptions.find((option) =>
      mechanismOptionMatches(field.name, option, raw, fieldLabels));
    if (exactOption && !optionUsesMechanismFieldSlider(field, exactOption)) {
      fields[field.name] = exactOption;
      continue;
    }

    if (usesMechanismFieldSlider(field)) {
      const cm = parseChainLengthCm(raw, field.unit);
      if (cm == null) continue;
      const legacySelection = raw.split(/\s+—\s+/, 1)[0]?.trim();
      const selection = enabledOptions.find((option) =>
        mechanismOptionMatches(field.name, option, legacySelection ?? '', fieldLabels))
        ?? enabledOptions.find((option) => parseChainLengthCm(option, field.unit) === cm)
        ?? field.numeric?.find((option) => enabledOptions.includes(option))
        ?? mechanismFieldSliderOptions(field)[0]
        ?? 'Ползунок';
      fields[field.name] = selection;
      values[field.name] = chainLengthMetersValue(cm);
      continue;
    }

    const [selection, numericTail] = raw.split(/\s+—\s+/, 2);
    const selected = selection?.trim();
    if (!selected || !enabledOptions.includes(selected)) continue;
    fields[field.name] = selected;
    if (field.numeric?.includes(selected) && numericTail) {
      const numeric = numericTail.match(/\d+(?:[.,]\d+)?/)?.[0];
      if (numeric) values[field.name] = numeric.replace(',', '.');
    }
  }
  return { fields, values };
}

/** Готовая подпись механизма → снапшот mechanismName изделия.
 *  MOTORS получает имя выбранного мотора и при наличии дописывает настроенные
 *  поля; дефолтный первый NONE-тип → null (как раньше).
 *  corniceKey — учитываем только поля, видимые для выбранного карниза. */
export function composeMechanismLabel(
  types: PbMechanismType[],
  tp: PbMechanismType | null,
  fields: Record<string, string>,
  corniceKey: string | null = null,
  values: Record<string, string> = {},
  motorName: string | null = null,
  fieldLabels: Record<string, string> = {},
  sectionCode?: string | null,
): string | null {
  if (!tp) return null;
  if (tp.kind === 'FIELDS' || tp.kind === 'MOTORS') {
    const baseName = tp.kind === 'MOTORS' ? motorName : tp.name;
    if (!baseName) return null;
    const fs = visibleMechFields(tp, corniceKey, sectionCode);
    if (!fs.length) return baseName; // для этого карниза полей нет → только мотор/тип
    if (!fs.every((f) => {
      const selected = fields[f.name];
      return !!selected && mechanismFieldEnabledOptions(f).includes(selected);
    })) return null; // не все видимые поля заполнены допустимыми вариантами
    return `${baseName} (${fs.map((f) => {
      const sel = fields[f.name]!;
      const fieldLabel = mechanismFieldDisplayLabel(f.name, fieldLabels);
      const selectedLabel = mechanismOptionDisplayLabel(f.name, sel, fieldLabels);
      if (optionUsesMechanismFieldSlider(f, sel)) {
        const value = (values[f.name] ?? '').trim();
        if (!value) return `${fieldLabel}: —`;
        return `${fieldLabel}: ${formatChainLengthCm(chainLengthCmFromState(f, sel, value))}`;
      }
      // Числовой вариант («Кастомный») → дописываем «— N ед»; иначе просто вариант.
      const val = f.numeric?.includes(sel) ? (values[f.name] ?? '').trim() : '';
      const tail = val ? ` — ${val}${f.unit ? ` ${f.unit}` : ''}` : '';
      return `${fieldLabel}: ${selectedLabel}${tail}`;
    }).join('; ')})`;
  }
  return tp.id === types[0]?.id ? null : tp.name;
}

/** Выбор завершён: у MOTORS обязателен мотор, а у MOTORS/FIELDS — все ВИДИМЫЕ
 *  характеристики (и, если выбранный вариант числовой — введено значение). */
export function mechanismStepOk(
  tp: PbMechanismType | null,
  s: { pbMechRowId: string | null; pbMechFields: Record<string, string>; pbMechValues?: Record<string, string> },
  corniceKey: string | null = null,
  sectionCode?: string | null,
): boolean {
  if (!tp || tp.kind === 'NONE') return true;
  if (tp.kind === 'MOTORS' && !s.pbMechRowId) return false;
  return visibleMechFields(tp, corniceKey, sectionCode).every((f) => {
    const sel = s.pbMechFields[f.name];
    if (!sel || !mechanismFieldEnabledOptions(f).includes(sel)) return false;
    if (optionUsesMechanismFieldSlider(f, sel)) {
      return parseChainLengthCm(s.pbMechValues?.[f.name], f.unit) != null;
    }
    // Числовой вариант — обязателен ввод числа.
    if (f.numeric?.includes(sel)) return !!(s.pbMechValues?.[f.name] ?? '').trim();
    return true;
  });
}
